import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';


const REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

export default function DataRefreshIndicator({ onRefresh, className = '' }) {
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [secondsAgo, setSecondsAgo] = useState(0);

  const computeSecondsAgo = useCallback(() => {
    setSecondsAgo(Math.floor((Date.now() - lastRefreshed?.getTime()) / 1000));
  }, [lastRefreshed]);

  useEffect(() => {
    computeSecondsAgo();
    const ticker = setInterval(computeSecondsAgo, 30000);
    return () => clearInterval(ticker);
  }, [computeSecondsAgo]);

  // Auto-refresh every 15 minutes
  useEffect(() => {
    const autoTimer = setInterval(() => {
      handleRefresh(true);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(autoTimer);
  }, []);

  const handleRefresh = async (auto = false) => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh?.();
    } finally {
      setLastRefreshed(new Date());
      setSecondsAgo(0);
      setIsRefreshing(false);
    }
  };

  const getStatusColor = () => {
    if (isRefreshing) return 'var(--color-warning)';
    if (secondsAgo < 60) return 'var(--color-success)';
    if (secondsAgo < 600) return 'var(--color-secondary)';
    return 'var(--color-accent)';
  };

  const getStatusLabel = () => {
    if (isRefreshing) return 'Refreshing…';
    if (secondsAgo < 60) return 'Live';
    if (secondsAgo < 3600) return `${Math.floor(secondsAgo / 60)}m ago`;
    return `${Math.floor(secondsAgo / 3600)}h ago`;
  };

  const getStatusText = () => {
    if (isRefreshing) return 'Fetching MLS data…';
    if (secondsAgo < 60) return 'MLS data is current';
    if (secondsAgo < 600) return 'Data refreshes every 15 min';
    return 'Data may be stale — refresh recommended';
  };

  return (
    <div className={`flex items-center gap-2 ${className}`} title={getStatusText()}>
      <div className="refresh-indicator">
        <span
          className="refresh-dot"
          style={{ backgroundColor: getStatusColor() }}
        />
        <span
          className="font-caption text-xs"
          style={{ color: getStatusColor() }}
        >
          {getStatusLabel()}
        </span>
      </div>

      <button
        onClick={() => handleRefresh(false)}
        disabled={isRefreshing}
        aria-label="Refresh MLS data"
        className="flex items-center justify-center w-7 h-7 rounded-md
                   hover:bg-muted transition-standard disabled:opacity-50 disabled:cursor-not-allowed
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Icon
          name="RefreshCw"
          size={13}
          color="var(--color-muted-foreground)"
          className={isRefreshing ? 'animate-spin' : ''}
        />
      </button>
    </div>
  );
}